import type {
  AccommodationsByCounty,
  CapacityByYear,
  CountySlug,
  IsoCode,
  Manifest,
  PeriodKey,
  Settlement,
  TravelData,
  AgeData,
  EconomyData,
} from '@/types/data'
import {
  loadAccommodationsByCounty,
  loadCapacity,
  loadManifest,
  loadPeriod,
  loadAges,
  loadEconomy,
  loadSettlements,
  loadTravel,
} from './dataClient'

export interface NamedCounty {
  slug: CountySlug
  name: string
}

export interface NamedCountry {
  iso: IsoCode
  name: string
}

/** Domestic/foreign night and guest series for every county, indexed [countyIndex][periodIndex]. */
export interface CountyMatrices {
  nightsD: (number | null)[][]
  nightsF: (number | null)[][]
  guestsD: (number | null)[][]
  guestsF: (number | null)[][]
}

/**
 * Everything the map needs, reshaped from the pipeline's per-period files into
 * the dense matrices the renderer indexes by position.
 *
 * Monthly and yearly are genuinely separate sources here (STADAT publishes them
 * from different tables with different histories), not one derived from the
 * other — unlike a demo dataset with one uniform year×12 grid, our monthly
 * coverage (2021–) is a strict subset of our yearly coverage (2000–), and a
 * pre-2021 year has no monthly figures to sum in the first place.
 */
export interface RealData {
  manifest: Manifest
  monthlyPeriods: PeriodKey[]
  yearlyPeriods: PeriodKey[]
  counties: NamedCounty[]
  countries: NamedCountry[]
  monthly: CountyMatrices
  yearly: CountyMatrices
  /** Sending-country nights, indexed [countryIndex][periodIndex]. */
  countryMonthly: (number | null)[][]
  countryYearly: (number | null)[][]
  /**
   * The 100 settlements with the most certified accommodations (the capital
   * and the county seats always among them) — real population and real geographic
   * placement, no per-settlement tourism figure (KSH does not publish one
   * as open data). See scripts/settlements.ts.
   */
  settlements: Settlement[]
  /** Certified-accommodation counts by county, from the same real register. */
  accommodationsByCounty: AccommodationsByCounty
  /**
   * Quarterly travel-survey figures: purpose of travel, and what each sending
   * country spends here. A different instrument from the accommodation
   * register — trips, not nights — so the two are never added together.
   * Null when the file is missing; every consumer must cope with that.
   */
  travel: TravelData | null
  /**
   * Age split of residents' own travel, from Eurostat — the one question KSH
   * never asks. Null when the file is missing.
   */
  ages: AgeData | null
  /**
   * Tourism's weight in the economy (Tourism Satellite Account). Annual and
   * published about two years in arrears, so it is context rather than news.
   */
  economy: EconomyData | null
  /**
   * Bed places by county and year (KSH, July 31 snapshot). The supply side —
   * without it guest-nights cannot say whether a county is actually full.
   */
  capacity: CapacityByYear
}

function emptyMatrix(rows: number, cols: number): (number | null)[][] {
  return Array.from({ length: rows }, () => new Array<number | null>(cols).fill(null))
}

export async function loadRealData(): Promise<RealData> {
  const manifest = await loadManifest()

  const counties: NamedCounty[] = Object.entries(manifest.countyNames).map(([slug, name]) => ({
    slug: slug as CountySlug,
    name,
  }))
  const countries: NamedCountry[] = Object.entries(manifest.countryNames)
    .map(([iso, name]) => ({ iso, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'))

  const monthlyPeriods = manifest.periods.monthly
  const yearlyPeriods = manifest.periods.yearly

  // Both of the trailing two are supplementary layers rather than the map's
  // core data, and both are produced by steps that may not have run yet (the
  // accommodation crawl takes ~30 minutes). A missing file must degrade that
  // one layer, not block the page — during `pnpm dev` the SPA fallback returns
  // index.html for a missing path, which then throws in JSON.parse.
  const optional = <T>(load: () => Promise<T>, fallback: T, hint: string) =>
    load().catch((err: unknown) => {
      console.warn(`${hint}:`, err)
      return fallback
    })

  const [
    monthlyData,
    yearlyData,
    settlements,
    accommodationsByCounty,
    capacity,
    travel,
    ages,
    economy,
  ] = await Promise.all([
    Promise.all(monthlyPeriods.map((p) => loadPeriod('monthly', p))),
    Promise.all(yearlyPeriods.map((p) => loadPeriod('yearly', p))),
    loadSettlements(),
    optional(
      loadAccommodationsByCounty,
      {} as AccommodationsByCounty,
      'Szálláshely-nyilvántartás nem elérhető (pnpm data:settlements?)',
    ),
    optional(loadCapacity, {} as CapacityByYear, 'Férőhely-adat nem elérhető (pnpm data?)'),
    optional(loadTravel, null, 'Utazási/költési adat nem elérhető (pnpm data:travel?)'),
    optional(loadAges, null, 'Korcsoportos adat nem elérhető (pnpm data:ages?)'),
    optional(loadEconomy, null, 'Szatellit számla nem elérhető (pnpm data:economy?)'),
  ])

  /**
   * Countries that only exist in the outbound direction. The map's country
   * list comes from the inbound table's "kiemelt országok", which does not
   * include Croatia, Serbia, Slovenia or Greece — all of them among the
   * places Hungarians travel to most. Without this the reversed map would
   * silently drop the second-most-visited destination in the country.
   *
   * They are appended with no inbound series, so the inbound view is
   * unchanged: a country with no nights reported reads as no data, which is
   * exactly what it is.
   */
  if (travel) {
    const known = new Set(countries.map((c) => c.iso))
    for (const entry of travel.outbound.countries) {
      for (const iso of entry.isos) {
        if (known.has(iso)) continue
        known.add(iso)
        countries.push({ iso, name: entry.label })
      }
    }
    countries.sort((a, b) => a.name.localeCompare(b.name, 'hu'))
  }

  function buildCountyMatrices(
    periodsData: Awaited<ReturnType<typeof loadPeriod>>[],
  ): CountyMatrices {
    const matrices: CountyMatrices = {
      nightsD: emptyMatrix(counties.length, periodsData.length),
      nightsF: emptyMatrix(counties.length, periodsData.length),
      guestsD: emptyMatrix(counties.length, periodsData.length),
      guestsF: emptyMatrix(counties.length, periodsData.length),
    }
    periodsData.forEach((data, pi) => {
      counties.forEach((county, ci) => {
        const area = data.byCounty[county.slug]
        if (!area) return
        matrices.nightsD[ci]![pi] = area.nights.domestic
        matrices.nightsF[ci]![pi] = area.nights.foreign
        matrices.guestsD[ci]![pi] = area.guests.domestic
        matrices.guestsF[ci]![pi] = area.guests.foreign
      })
    })
    return matrices
  }

  function buildCountryMatrix(
    periodsData: Awaited<ReturnType<typeof loadPeriod>>[],
  ): (number | null)[][] {
    const matrix = emptyMatrix(countries.length, periodsData.length)
    periodsData.forEach((data, pi) => {
      countries.forEach((country, ki) => {
        matrix[ki]![pi] = data.byCountry[country.iso]?.nights ?? null
      })
    })
    return matrix
  }

  return {
    manifest,
    monthlyPeriods,
    yearlyPeriods,
    counties,
    countries,
    monthly: buildCountyMatrices(monthlyData),
    yearly: buildCountyMatrices(yearlyData),
    countryMonthly: buildCountryMatrix(monthlyData),
    countryYearly: buildCountryMatrix(yearlyData),
    settlements,
    accommodationsByCounty,
    capacity,
    travel,
    ages,
    economy,
  }
}
